// Увольнение. Модалка — ровно тот случай, ради которого она оставлена (правило 4):
// необратимое действие, которое надо подтвердить. Человек не удаляется, но обратной кнопки
// «вернуть» у увольнения нет: закрываются назначения, и долги переходят в разряд
// «уволен с непогашенным».
//
// Подтверждение обязано называть ПОСЛЕДСТВИЕ, а не спрашивать «вы уверены». Уверенность
// человека не информация; «долг останется висеть и потребует решения руководства» —
// информация.
//
// Причина увольнения обязательна и на сервере (FireIn.reason, minLength 1), и здесь.
// Проверяем на этой стороне не вместо сервера, а чтобы человек узнал об этом до отправки,
// а не отказом.

import { useState } from 'react';

import { RiLockLine, RiLogoutBoxLine } from '@remixicon/react';

import type { EmployeeOut } from '@/shared/api/endpoints/people';
import { useAuthStore } from '@/shared/auth/store';
import { ДОЛГИ_НЕ_ПЕРЕНЕСЕНЫ, useПереносДолгов, состояниеПереноса } from '@/shared/debts/перенос';
import { can } from '@/shared/rbac/permissions';
import * as Button from '@/shared/ui/button';
import { DateField, todayIso } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Modal from '@/shared/ui/modal';
import { TextField } from '@/shared/ui/text-field';

import { useFireMutation } from '../queries';

export function FireDialog({ employee, canEdit }: { employee: EmployeeOut; canEdit: boolean }) {
  const [open, setOpen] = useState(false);
  // «Долгов нет, закрывать нечего» в окне НЕОБРАТИМОГО действия — ровно та фраза, которую
  // 23.09.2026 заказчик поймал на карточке: долгов в программе не было потому, что их не
  // переносили. Признак переноса спрашивается только у тех, кому долги открыты.
  const видитДолги = can(
    useAuthStore((state) => state.permissions),
    'core.debt.view',
  );
  const перенос = состояниеПереноса(useПереносДолгов(видитДолги && open));
  const [firedAt, setFiredAt] = useState(todayIso);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>(undefined);
  const mutation = useFireMutation(employee.id);

  function confirm() {
    if (reason.trim() === '') {
      setReasonError(
        'Причина обязательна: без неё запись об увольнении не сохранится, а через полгода никто не вспомнит, почему человек ушёл.',
      );
      return;
    }
    setReasonError(undefined);
    mutation.mutate(
      { fired_at: firedAt, reason: reason.trim() },
      { onSuccess: () => setOpen(false) },
    );
  }

  return (
    <Modal.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          mutation.reset();
          setReasonError(undefined);
        }
      }}
    >
      <Modal.Trigger asChild>
        <Button.Root variant='error' mode='stroke' size='xsmall' type='button'>
          <Button.Icon as={RiLogoutBoxLine} />
          Уволить
        </Button.Root>
      </Modal.Trigger>
      <Modal.Content>
        <Modal.Header
          icon={canEdit ? RiLogoutBoxLine : RiLockLine}
          // Имя стоит ОТДЕЛЬНОЙ строкой, а не внутри вопроса. По-русски «Уволить Багровев
          // Роман Фёдорович?» — это именительный падеж там, где нужен винительный: «уволить
          // Багровева Романа Фёдоровича». Склонять фамилии в коде нельзя (женские, иностранные
          // и несклоняемые дают ошибку чаще, чем правильный ответ), а неверный падеж в окне
          // необратимого действия читается как небрежность ко всему остальному.
          title={canEdit ? 'Уволить сотрудника?' : 'Уволить нельзя'}
          description={
            canEdit
              ? `${employee.display_name}. Действие необратимо: обратной кнопки у увольнения нет.`
              : 'Увольнение выполняют кадры — у них есть отдельное право на это действие.'
          }
        />
        <Modal.Body>
          {canEdit ? (
            <div className='flex flex-col gap-3'>
              <ul className='flex flex-col gap-1.5 text-paragraph-sm text-text-sub-600'>
                <li>Живые назначения закроются, в новые табели человек не попадёт.</li>
                <li>
                  {employee.has_debt
                    ? 'На человеке есть непогашенный долг: он останется и потребует решения руководства — сам он не спишется.'
                    : перенос === 'перенесены'
                      ? 'Непогашенных долгов на человеке нет, закрывать после увольнения нечего.'
                      : перенос === 'не перенесены'
                        ? `В программе непогашенных долгов на человеке нет. ${ДОЛГИ_НЕ_ПЕРЕНЕСЕНЫ.replace('сюда ещё', 'ещё')} Перед увольнением сверьте их с книгой.`
                        : 'В программе непогашенных долгов на человеке нет.'}
                </li>
                <li>Карточка и вся история остаются: расчётные листы и ведомости не трогаются.</li>
              </ul>
              <DateField
                id='fire-date'
                label='Дата увольнения'
                required
                value={firedAt}
                onChange={setFiredAt}
                hint='Последний рабочий день. По нему закроются назначения.'
              />
              <TextField
                id='fire-reason'
                label='Причина увольнения'
                required
                value={reason}
                onChange={setReason}
                placeholder='Например: по собственному желанию'
                error={reasonError}
                hint='Останется в карточке навсегда и попадёт в историю статусов.'
              />
            </div>
          ) : (
            <p className='text-paragraph-sm text-text-sub-600'>
              У вашей учётной записи нет права на увольнение, поэтому кнопка ничего не изменит.
              Попросите администратора выдать это право — либо передайте увольнение кадрам.
            </p>
          )}
          {mutation.isError && <ErrorAlert error={mutation.error} className='mt-3' />}
        </Modal.Body>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button.Root variant='neutral' mode='stroke' size='small' className='w-full'>
              {canEdit ? 'Отмена' : 'Понятно'}
            </Button.Root>
          </Modal.Close>
          {canEdit && (
            <Button.Root
              variant='error'
              mode='filled'
              size='small'
              className='w-full'
              type='button'
              onClick={confirm}
            >
              {mutation.isPending ? 'Увольняем…' : 'Уволить'}
            </Button.Root>
          )}
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}
